import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { Mail, MapPin, AlertCircle } from 'lucide-react';

const Complaints = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="complaints"
        defaultTitle="Complaints Procedure | House Plan Direct"
        defaultDescription="Complaints procedure for House Plan Direct floor plan services."
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8" data-testid="complaints-header">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Complaints Procedure</h1>
          <p className="text-lg text-gray-600">
            We take all complaints seriously and are committed to resolving issues fairly and promptly.
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Before Making a Complaint</h3>
              <p className="text-sm text-gray-700">
                If you have a concern or issue with our service, we encourage you to contact us first at{' '}
                <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">
                  web@houseplandirect.com
                </a>
                {' '}so we can resolve the matter quickly and informally.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 1: Submit Your Complaint</h2>
            <p className="text-gray-700 mb-4">
              All complaints must be submitted in writing via email to:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline font-semibold text-lg">
                web@houseplandirect.com
              </a>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 2: Information Required</h2>
            <p className="text-gray-700 mb-4">Your complaint should include the following information:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>Your full name and contact details</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>Your order number (if applicable)</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>A clear description of the issue or concern</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>Any relevant dates, documents, or evidence</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>What resolution you are seeking</span>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-3">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 3: Acknowledgment</h2>
            <p className="text-gray-700">
              We will acknowledge receipt of your complaint within <strong>2 business days</strong> and provide you with a complaint reference number.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 4: Investigation</h2>
            <p className="text-gray-700">
              We will investigate your complaint thoroughly, which may include reviewing records, speaking with staff, and examining any evidence provided.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-5">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 5: Response</h2>
            <p className="text-gray-700">
              We will provide you with a full written response within <strong>10 business days</strong> of receiving your complaint. This response will include:
            </p>
            <ul className="mt-4 space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>Our findings from the investigation</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>An explanation of our decision</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>Any actions we will take to resolve the issue</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>Information about escalation options if you are not satisfied</span>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 6: Escalation</h2>
            <p className="text-gray-700 mb-4">
              If you are not satisfied with our initial response, you may escalate your complaint to our Managing Director by replying to the response email and stating that you wish to escalate the matter.
            </p>
            <p className="text-gray-700">
              The Managing Director will review your complaint and our initial response, and provide a further written response within <strong>10 business days</strong>.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-7">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 7: Final Decision</h2>
            <p className="text-gray-700">
              The Managing Director's decision is final and concludes our internal complaints procedure.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step 8: External Resolution</h2>
            <p className="text-gray-700">
              If you remain dissatisfied after exhausting our internal complaints procedure, you may seek independent advice or consider alternative dispute resolution options as appropriate under UK consumer law.
            </p>
          </section>
        </div>

        {/* Contact Information */}
        <div className="mt-12 bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="contact-info">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Email:</p>
                <p className="text-gray-700">General enquiries and complaints: <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">web@houseplandirect.com</a></p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Postal Address:</p>
                <p className="text-gray-700">
                  House Plan Direct<br />
                  PO Box 686<br />
                  Exeter, EX2 9PD<br />
                  United Kingdom
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Complaints;